import guideMarkdown from "@/content/user-guide.md?raw";
import { useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { useLocation } from "react-router-dom";
import remarkGfm from "remark-gfm";

/** Anchor id from a heading's text — keeps Korean letters, hyphenates the rest. */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
}

const TOC = guideMarkdown
  .split("\n")
  .filter((line) => line.startsWith("## "))
  .map((line) => line.slice(3).trim());

/**
 * Public usage guide: the operator walkthrough (with the guest experience
 * woven in), rendered from markdown with real product screenshots.
 */
export function UserGuidePage() {
  const { hash } = useLocation();

  // Deep links like /guide#섹션 land before the browser can resolve the anchor
  // (headings render client-side), so scroll manually — once immediately, once
  // after lazy-loaded screenshots have had a moment to shift the layout.
  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    const scrollToAnchor = () => document.getElementById(id)?.scrollIntoView();
    scrollToAnchor();
    const settle = window.setTimeout(scrollToAnchor, 400);
    return () => window.clearTimeout(settle);
  }, [hash]);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 md:py-16">
      <header className="animate-rise max-w-2xl">
        <p className="eyebrow-brass">User Guide</p>
        <h1 className="mt-3 break-keep font-display text-3xl font-medium tracking-tight text-foreground sm:text-4xl">
          처음 시작하는 운영자를 위한 가이드
        </h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          로그인부터 게스트 응대 시작까지, 실제 화면과 함께 순서대로 따라가는 설정 안내입니다.
        </p>
      </header>

      <div className="mt-10 grid gap-10 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="hidden lg:block">
          <nav
            aria-label="가이드 목차"
            className="sticky top-24 space-y-1 border-l border-border/70 pl-4"
          >
            <p className="eyebrow mb-3">목차</p>
            {TOC.map((title) => (
              <a
                key={title}
                href={`#${slugify(title)}`}
                className="block py-1 text-[13px] leading-snug text-muted-foreground transition-colors hover:text-foreground"
              >
                {title}
              </a>
            ))}
          </nav>
        </aside>

        <article className="doc-prose animate-rise min-w-0 max-w-3xl [animation-delay:80ms]">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h2: ({ children }) => (
                <h2 id={slugify(String(children))} className="scroll-mt-24">
                  {children}
                </h2>
              ),
              img: ({ src, alt }) => <img src={src} alt={alt ?? ""} loading="lazy" />,
            }}
          >
            {guideMarkdown}
          </ReactMarkdown>
        </article>
      </div>
    </div>
  );
}
